import CONFIG from '../config/config';

const CRISP_SCRIPT_SRC = 'https://client.crisp.chat/l.js';

type CrispEvent =
  | 'session:loaded'
  | 'message:received'
  | 'chat:opened'
  | 'chat:closed';

export type CrispReceivedMessage = {
  origin?: string;
};

type CrispEventCallback = (...args: unknown[]) => void;

type CrispCommand = [string, string, ...unknown[]];

interface CrispSdk {
  push: (command: CrispCommand) => void;
  get?: (method: string, key?: string) => unknown;
  is?: (method: string) => boolean;
}

declare global {
  interface Window {
    $crisp?: CrispSdk | CrispCommand[];
    CRISP_WEBSITE_ID?: string;
    CRISP_TOKEN_ID?: string | null;
  }
}

const eventListeners = new Map<CrispEvent, Set<CrispEventCallback>>();
const boundEvents = new Set<CrispEvent>();
let scriptInjected = false;
let awaitingToken = false;

function ensureQueue(): CrispSdk {
  if (typeof window === 'undefined') {
    return { push: () => undefined };
  }
  if (!window.$crisp) {
    window.$crisp = [] as CrispCommand[];
  }
  return window.$crisp as CrispSdk;
}

function isScriptPresent(): boolean {
  return Boolean(document.querySelector(`script[src="${CRISP_SCRIPT_SRC}"]`));
}

export function waitForCrispToken(): void {
  awaitingToken = true;
}

export function loadCrisp(websiteId: string = CONFIG.CRISP_WEBSITE_ID, tokenId?: string): void {
  if (typeof window === 'undefined') return;

  awaitingToken = false;
  window.CRISP_WEBSITE_ID = websiteId;
  ensureQueue();
  hideChat();

  if (scriptInjected || isScriptPresent()) {
    scriptInjected = true;
    if (tokenId && window.CRISP_TOKEN_ID !== tokenId) {
      window.CRISP_TOKEN_ID = tokenId;
      ensureQueue().push(['do', 'session:reset']);
    }
    return;
  }

  if (tokenId) {
    window.CRISP_TOKEN_ID = tokenId;
  }

  scriptInjected = true;
  const script = document.createElement('script');
  script.src = CRISP_SCRIPT_SRC;
  script.async = true;
  document.getElementsByTagName('head')[0].appendChild(script);
}

export function hideChat(): void {
  ensureQueue().push(['do', 'chat:hide']);
}

export function showChat(): void {
  ensureQueue().push(['do', 'chat:show']);
}

export function openChat(): void {
  if (!awaitingToken) {
    loadCrisp();
  }
  showChat();
  ensureQueue().push(['do', 'chat:open']);
}

export function setCrispLocale(locale: string): void {
  const language = locale.split('-')[0].toLowerCase();
  ensureQueue().push(['config', 'locale', [language]]);
}

export function getUnreadCount(): number {
  if (typeof window === 'undefined') return 0;
  const sdk = window.$crisp as CrispSdk | undefined;
  if (typeof sdk?.get !== 'function') return 0;

  try {
    const count = sdk.get('chat:unread:count');
    return typeof count === 'number' && Number.isFinite(count) ? count : 0;
  } catch (error) {
    console.error('Failed to read Crisp unread count', { error });
    return 0;
  }
}

export function getSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  const sdk = window.$crisp as CrispSdk | undefined;
  if (typeof sdk?.get !== 'function') return null;

  try {
    const sessionId = sdk.get('session:identifier');
    return typeof sessionId === 'string' && sessionId.length > 0 ? sessionId : null;
  } catch (error) {
    console.error('Failed to read Crisp session id', { error });
    return null;
  }
}

export function isChatOpened(): boolean {
  const sdk = window.$crisp as CrispSdk | undefined;
  return typeof sdk?.is === 'function' && sdk.is('chat:opened') === true;
}

export function setUserEmail(email: string, signature?: string): void {
  ensureQueue().push(['set', 'user:email', signature ? [email, signature] : [email]]);
}

export function setUserNickname(nickname: string): void {
  ensureQueue().push(['set', 'user:nickname', [nickname]]);
}

export function setUserCompany(name: string): void {
  ensureQueue().push(['set', 'user:company', [name]]);
}

export function setSessionData(entries: Array<[string, string | number | boolean]>): void {
  if (entries.length === 0) return;
  ensureQueue().push(['set', 'session:data', [entries]]);
}

export function setSessionSegments(segments: string[]): void {
  if (segments.length === 0) return;
  ensureQueue().push(['set', 'session:segments', [segments]]);
}

export function resetSession(): void {
  if (typeof window !== 'undefined') {
    window.CRISP_TOKEN_ID = null;
  }
  ensureQueue().push(['do', 'session:reset']);
}

type VisitorIdentity = {
  email?: string;
  emailSignature?: string;
  tenantId?: string;
  storeName?: string;
  storePlan?: string;
  storeEmail?: string;
};

export function identifyVisitor(identity: VisitorIdentity): void {
  if (identity.email) {
    setUserEmail(identity.email, identity.emailSignature);
  }
  if (identity.storeName) {
    setUserNickname(identity.storeName);
    setUserCompany(identity.storeName);
  }

  const sessionEntries: Array<[string, string | number | boolean]> = [];
  if (identity.tenantId) sessionEntries.push(['store_id', identity.tenantId]);
  if (identity.storeName) sessionEntries.push(['store_name', identity.storeName]);
  if (identity.storePlan) sessionEntries.push(['store_plan', identity.storePlan]);
  if (identity.storeEmail) sessionEntries.push(['store_email', identity.storeEmail]);
  setSessionData(sessionEntries);

  if (identity.storePlan) {
    setSessionSegments([identity.storePlan]);
  }
}

export function onCrispEvent(event: CrispEvent, callback: CrispEventCallback): () => void {
  let listeners = eventListeners.get(event);
  if (!listeners) {
    listeners = new Set();
    eventListeners.set(event, listeners);
  }
  listeners.add(callback);

  if (!boundEvents.has(event)) {
    boundEvents.add(event);
    ensureQueue().push(['on', event, (...args: unknown[]) => {
      eventListeners.get(event)?.forEach((listener) => listener(...args));
    }]);
  }

  return () => {
    listeners?.delete(callback);
  };
}
